import { useState } from 'react';
import { Check, Pill, Plus, X } from 'lucide-react';
import { uid } from '../state';
import { supplementStreak } from '../body';
import { FONT_BODY, FONT_MONO } from '../theme';
import { Button, Input } from '../ui';

/* Daily supplements, as one tap. Sits with the meal log because that's the
 * daily check-in you already open — and it tracks the selected day, so a
 * forgotten tick can be fixed on yesterday. Deliberately just buttons: it
 * answers "did I take it?" and gets out of the way. */
export function SupplementRow({ t, state, setState, dayKey }) {
  const [managing, setManaging] = useState(false);
  const [draft, setDraft] = useState('');
  const items = state.supplements?.items || [];
  const log = state.supplements?.log || {};
  const taken = log[dayKey] || [];

  const toggle = (id) => setState(prev => {
    const next = { ...(prev.supplements?.log || {}) };
    const day = new Set(next[dayKey] || []);
    if (day.has(id)) day.delete(id); else day.add(id);
    if (day.size) next[dayKey] = [...day]; else delete next[dayKey];
    return { ...prev, supplements: { items: prev.supplements?.items || [], log: next } };
  });

  const addItem = () => {
    const name = draft.trim();
    if (!name) return;
    setState(prev => {
      const list = prev.supplements?.items || [];
      if (list.some(i => (i.name || '').toLowerCase() === name.toLowerCase())) return prev;
      return {
        ...prev,
        supplements: {
          items: [...list, { id: uid('supp'), name }],
          log: prev.supplements?.log || {},
        },
      };
    });
    setDraft('');
  };

  const removeItem = (id) => setState(prev => {
    // Drop it from history too, so a removed item can't linger as an orphan
    // id inside every past day.
    const nextLog = {};
    for (const [k, ids] of Object.entries(prev.supplements?.log || {})) {
      const kept = (ids || []).filter(x => x !== id);
      if (kept.length) nextLog[k] = kept;
    }
    return {
      ...prev,
      supplements: { items: (prev.supplements?.items || []).filter(i => i.id !== id), log: nextLog },
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {items.map(item => {
        const on = taken.includes(item.id);
        const streak = supplementStreak(log, item.id, dayKey);
        return (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button onClick={() => toggle(item.id)} aria-pressed={on}
            title={on ? `${item.name} — taken` : `${item.name} — not taken yet`}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              padding: '10px 14px', borderRadius: 10,
              border: on ? `1px solid ${t.accentBorder}` : `1px dashed ${t.cardBorder}`,
              background: on ? t.accentSoft : 'transparent',
              color: on ? t.accent : t.textMuted,
              fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: 600, transition: 'all 0.15s',
            }}>
            <Pill size={14} style={{ flexShrink: 0 }} />
            {item.name}
            <span style={{
              width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
              border: `1.5px solid ${on ? t.accent : t.cardBorder}`,
              background: on ? t.accent : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {on && <Check size={11} color="#fff" strokeWidth={3} />}
            </span>
            {streak > 1 && (
              <span style={{ fontFamily: FONT_MONO, fontSize: 10, opacity: 0.8 }}>{streak}d</span>
            )}
          </button>
          {managing && (
            <button onClick={() => removeItem(item.id)} title={`Remove ${item.name}`}
              aria-label={`Remove ${item.name}`}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: '0 2px', display: 'flex' }}>
              <X size={13} />
            </button>
          )}
          </div>
        );
      })}

        {/* Add — hidden behind a single '+' so the normal state stays just
            the buttons you tap. */}
        <button onClick={() => setManaging(v => !v)}
          title={managing ? 'Done' : 'Add something you take daily'}
          aria-label={managing ? 'Done editing' : 'Add supplement'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            padding: '10px 12px', borderRadius: 10,
            border: `1px dashed ${t.cardBorder}`, background: 'transparent',
            color: managing ? t.accent : t.textFaint,
            fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: 600,
          }}>
          {managing ? 'Done' : <Plus size={14} />}
        </button>
      </div>

      {managing && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Input t={t} autoFocus placeholder="e.g. Medicatie, Vitamine D…" value={draft} maxLength={40}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
            style={{ flex: 1 }} />
          <Button t={t} size="sm" variant="primary" disabled={!draft.trim()} onClick={addItem} style={{ flexShrink: 0 }}>
            <Plus size={14} /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
